import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert,
  Chip, Stack, Skeleton, Dialog, DialogContent, DialogActions, Divider,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  CheckCircle as VerifiedIcon,
  HelpOutline as UnverifiedIcon,
  HourglassEmpty as PendingIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { useNotificationStore } from '@/stores/notificationStore';
import { bankDetailsApi, BankDetailsChangeRequest } from '@/api/endpoints/hr';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import PageHelpIcon from '@/components/PageHelpIcon';

interface CountryBankRules {
  /** Human-readable hint shown under the account-number field. */
  accountHint: string;
  /** Exact length the account number must have, or 0 to skip the check. */
  accountLength: number;
  codeHint: string;
}

/**
 * Country-specific bank-account validation hints, keyed off the active
 * jurisdiction's currency (we don't have a country code on this hook, but
 * currency is a reliable proxy for the supported African jurisdictions).
 */
function bankRulesForCurrency(currency: string | null | undefined): CountryBankRules {
  switch ((currency || '').toUpperCase()) {
    case 'NGN':
      return {
        accountHint: 'Nigerian NUBAN account numbers are exactly 10 digits.',
        accountLength: 10,
        codeHint: 'CBN bank code (e.g. 058 for GTBank).',
      };
    case 'GHS':
      return {
        accountHint: 'Ghanaian account numbers are typically 13 digits.',
        accountLength: 13,
        codeHint: 'Bank sort / branch code.',
      };
    case 'KES':
      return {
        accountHint: 'Kenyan account numbers vary by bank (usually 10–13 digits).',
        accountLength: 0,
        codeHint: 'Bank / branch code.',
      };
    case 'ZAR':
      return {
        accountHint: 'South African account numbers are usually 9–11 digits.',
        accountLength: 0,
        codeHint: 'Universal branch code (6 digits).',
      };
    default:
      return {
        accountHint: 'Enter your full bank account number with no spaces.',
        accountLength: 0,
        codeHint: 'Your bank’s sort / branch code.',
      };
  }
}

export default function PaymentDetailsPage() {
  const { currencyCode } = useJurisdiction();
  const { showSuccess, showError } = useNotificationStore();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<BankDetailsChangeRequest>({
    bank_account_number: '',
    bank_name: '',
    bank_code: '',
  });

  const rules = useMemo(() => bankRulesForCurrency(currencyCode), [currencyCode]);

  const { data, isLoading } = useQuery({
    queryKey: ['my-bank-details'],
    queryFn: () => bankDetailsApi.getMine(),
    select: (res) => res.data,
  });

  const requestMutation = useMutation({
    mutationFn: () => bankDetailsApi.requestChange(form),
    onSuccess: () => {
      showSuccess('Request submitted — your HR admin will review');
      setDialogOpen(false);
      setForm({ bank_account_number: '', bank_name: '', bank_code: '' });
      qc.invalidateQueries({ queryKey: ['my-bank-details'] });
    },
    onError: (e: any) => {
      if (e?.response?.status === 409) {
        showError('Cannot update during active payroll run');
      } else {
        showError(e?.response?.data?.detail || 'Failed to submit request');
      }
    },
  });

  const accountLengthValid =
    rules.accountLength === 0 || form.bank_account_number.replace(/\s/g, '').length === rules.accountLength;
  const formValid =
    form.bank_account_number.trim().length > 0 &&
    form.bank_name.trim().length > 0 &&
    form.bank_code.trim().length > 0 &&
    accountLengthValid;

  const isVerified = !!data?.is_verified || !!data?.paystack_recipient_code;
  const pending = data?.pending_request || null;

  return (
    <Box>
      {/* Page Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Payment Details</Typography>
        <PageHelpIcon pageKey="payment-details" />
      </Stack>

      {/* Pending request banner */}
      {pending && (
        <Alert
          severity="warning"
          icon={<PendingIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight={TYPE.semibold}>
            Account change under review
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You requested to change your salary account
            {pending.bank_name ? ` to ${pending.bank_name}` : ''}
            {pending.bank_account_number_masked ? ` (${pending.bank_account_number_masked})` : ''}.
            Your HR admin will review it shortly.
          </Typography>
        </Alert>
      )}

      {/* Current bank details card */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={TYPE.semibold}>
              <BankIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'text-bottom', color: 'primary.main' }} />
              Salary Account
            </Typography>
            {isVerified ? (
              <Chip
                icon={<VerifiedIcon />}
                label="Verified"
                size="small"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<UnverifiedIcon />}
                label="Unverified"
                size="small"
                color="default"
                variant="outlined"
              />
            )}
          </Stack>

          {isLoading ? (
            <Skeleton variant="rectangular" height={92} />
          ) : !data?.bank_account_number_masked && !data?.bank_name ? (
            <Alert severity="info" icon={false}>
              No bank account on file yet. Use “Request Account Change” to add your salary account.
            </Alert>
          ) : (
            <Stack spacing={1.25} divider={<Divider flexItem />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Bank</Typography>
                <Typography variant="body2" fontWeight={TYPE.medium}>{data?.bank_name || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Account Number</Typography>
                <Typography variant="body2" fontWeight={TYPE.medium} sx={{ fontFamily: 'monospace' }}>
                  {data?.bank_account_number_masked || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Bank Code</Typography>
                <Typography variant="body2" fontWeight={TYPE.medium}>{data?.bank_code || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Paystack Verification</Typography>
                {isVerified ? (
                  <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" variant="outlined" />
                ) : (
                  <Chip icon={<UnverifiedIcon />} label="Unverified" size="small" color="default" variant="outlined" />
                )}
              </Box>
            </Stack>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={!!pending}
            >
              Request Account Change
            </Button>
            {pending && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                A change request is already pending review.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Request change dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>Request Account Change</BrandedDialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2.5}>
            <Alert severity="info" icon={false}>
              Changes to your salary account must be reviewed by your HR admin before they take
              effect. You cannot request a change while a payroll run is in progress.
            </Alert>
            <TextField
              size="small"
              label="New Account Number"
              required
              fullWidth
              value={form.bank_account_number}
              onChange={(e) => setForm({ ...form, bank_account_number: e.target.value.replace(/[^\d]/g, '') })}
              helperText={
                !accountLengthValid && form.bank_account_number.length > 0
                  ? `Expected ${rules.accountLength} digits. ${rules.accountHint}`
                  : rules.accountHint
              }
              error={!accountLengthValid && form.bank_account_number.length > 0}
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              size="small"
              label="Bank Name"
              required
              fullWidth
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            />
            <TextField
              size="small"
              label="Bank Code"
              required
              fullWidth
              value={form.bank_code}
              onChange={(e) => setForm({ ...form, bank_code: e.target.value })}
              helperText={rules.codeHint}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => requestMutation.mutate()}
            disabled={requestMutation.isPending || !formValid}
          >
            {requestMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
